import { SingleCombobox } from '@commenda/kakashi';
import { SalesTaxFilingFrequencyEnum } from '@prisma/client';
import { useMemo } from 'react';
import { FILING_FREQUENCY_CONFIG } from '../../constants';
import { FilingFrequencySelectorProps } from '../../types';

export function FilingFrequencySelector({
  value,
  onChange,
  disabled = false,
  error,
  availableFrequencies,
}: FilingFrequencySelectorProps) {
  const frequencyOptions = useMemo(() => {
    // If API provides available frequencies, filter to only those
    if (availableFrequencies && availableFrequencies.length > 0) {
      return FILING_FREQUENCY_CONFIG.filter((config) =>
        availableFrequencies.includes(config.value)
      ).map((config) => ({
        value: config.value,
        label: config.label,
      }));
    }

    // Fallback to all frequencies if no API options provided
    return FILING_FREQUENCY_CONFIG.map((config) => ({
      value: config.value,
      label: config.label,
    }));
  }, [availableFrequencies]);

  const handleChange = (selectedValue: string) => {
    // The combobox options come from FILING_FREQUENCY_CONFIG where every
    // `value` is typed SalesTaxFilingFrequencyEnum. The kakashi combobox strips
    // the generic and returns a plain string, so narrow by membership check
    // rather than a bare cast.
    if (!selectedValue) {
      onChange(null);
      return;
    }
    const match = frequencyOptions.find((o) => o.value === selectedValue);
    onChange(match ? match.value : null);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <label className="txt-small-medium text-text-secondary">
        Filing frequency
        <span className="txt-small-medium text-utility-violet-600 ml-0.5">
          *
        </span>
      </label>
      <SingleCombobox
        value={value ?? ''}
        setValue={handleChange}
        options={frequencyOptions}
        placeholder="Select frequency"
        searchPlaceholder="Search frequencies..."
        notFoundText="No frequencies found"
        disabled={disabled}
        canClearSelection={false}
        error={error}
        hideSearch
        _className="txt-medium-regular text-text-secondary h-11 px-3"
        renderTrigger={({ selectedOption, placeholder }) => (
          <span className="txt-medium-regular text-text-secondary">
            {selectedOption?.label || placeholder}
          </span>
        )}
      />
      {error && (
        <p className="txt-xs-regular text-utility-error-500">{error}</p>
      )}
    </div>
  );
}
